import gql from 'graphql-tag';
import React, { useContext } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from '@xo-union/tk-component-icons';
import { useApolloClient } from '@apollo/react-hooks';

import Card, { Action, Header } from '../Card';
import {
  EventsQuery,
  useEventRsvpDataQuery,
  useEventsQuery,
  useWeddingWebsiteQuery,
  WeddingWebsiteQuery,
  useUpdateRsvpPageHiddenMutation,
} from '../../generated/graphql';
import onDownloadRsvpResponses from '../../helpers/downloadRsvpResponses';
import removeGuestWeddingTypename from '../../helpers/removeGuestWeddingTypename';
import {
  dashboardRoute,
  manageRsvpSettingsRoute,
  previewPageRouteWithOpenEditRail,
  previewRoute,
  rsvpSettingsRoute,
} from '../../helpers/routes';
import { PageCardUIContext } from '../../PageCardUIContext';
import { TrackingContext } from '../../TrackingContext';
import CardContainer from '../CardContainer';
import InactiveRsvpCard from './InactiveRsvpCard';
import styles from './styles.scss';
import LinkButton from '../LinkButton';
import { weddingWebsiteQuery } from '../Dashboard';
import RsvpSummaryCard from '../RsvpSummary';
import { FeatureToggleEnum, useFeatureTogglesContext } from '../../context/FeatureTogglesContext';

const trackPageValue = 'RSVP';
const rsvpPreviewPageRoute = 'rsvp';

export const eventRsvpData = gql`
  query EventRsvpDataQuery {
    eventRsvpData {
      eventId
      guestCount
      acceptCount
      rejectCount
    }
  }
`;

export const updateRsvpPageHiddenMutation = gql`
  mutation UpdateRsvpPageHiddenMutation($guestWeddingInput: GDS_UpdateGuestWeddingInput!) {
    updateGuestWedding(input: $guestWeddingInput) {
      rsvpPageHidden
    }
  }
`;

const RsvpPageCard: React.FC = () => {
  const { data: gdsData } = useWeddingWebsiteQuery();
  const { data: rsvpData } = useEventRsvpDataQuery();
  const { data: eventData } = useEventsQuery();
  const [updateRsvpPageHidden] = useUpdateRsvpPageHiddenMutation();
  const guestWedding = React.useMemo(() => removeGuestWeddingTypename(gdsData?.guestWedding), [gdsData]);
  const rsvpPageHidden = gdsData?.guestWedding?.rsvpPageHidden;
  const client = useApolloClient();
  const history = useHistory();
  const { pathname } = useLocation();
  const { track } = React.useContext(TrackingContext);
  const { isEnabled } = useFeatureTogglesContext();
  const { isPageOpenObj, togglePageOpen } = useContext(PageCardUIContext);
  const open = isPageOpenObj?.rsvp;
  const shouldUseEditorRollout = isEnabled(FeatureToggleEnum.EditorRollout);

  const onHeaderClick = () => {
    track('Item Action Initiated', {
      action: 'edit',
      actionName: 'Edit Page',
      page: trackPageValue,
      item: null,
    });
    history.push(manageRsvpSettingsRoute());
  };

  const onPreviewClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    const editPreviewPath = previewPageRouteWithOpenEditRail(rsvpPreviewPageRoute);
    const isDashboardContext = pathname.startsWith(dashboardRoute) && !pathname.startsWith(previewRoute);

    if (shouldUseEditorRollout && isDashboardContext) {
      window.location.assign(editPreviewPath);
    } else {
      history.push(editPreviewPath);
    }

    track('Website Preview', { source: 'page', page: trackPageValue });
  };

  const onEditRsvpClick = () => {
    track('WWS Dashboard Interaction', {
      action: 'RSVP Settings Initiated',
      actionName: 'RSVP Settings Initiated',
    });
    history.push(rsvpSettingsRoute);
  };

  const toggleVisibility = async () => {
    const { data } = await updateRsvpPageHidden({
      variables: {
        guestWeddingInput: {
          rsvpPageHidden: !rsvpPageHidden,
        },
      },
    });

    if (data) {
      const { guestWedding } = client.readQuery<WeddingWebsiteQuery>({ query: weddingWebsiteQuery })!;

      client.writeQuery({
        query: weddingWebsiteQuery,
        data: { guestWedding: { ...guestWedding, ...data?.updateGuestWedding } },
      });
    }
  };

  const statusText = rsvpPageHidden ? 'Hidden' : 'Visible';

  const RsvpCard = rsvpPageHidden ? InactiveRsvpCard : RsvpSummaryCard;

  return (
    <CardContainer id="rsvp">
      <Card>
        <Header
          titleText="RSVP"
          statusText={statusText}
          onClick={onHeaderClick}
          previewOnClick={onPreviewClick}
          open={open}
          toggleOpen={() => togglePageOpen('rsvp')}
          guidance={false}
          toggleVisibility={toggleVisibility}
          hasDropdown
          rsvpPage
          as="h2"
        />
        {open ? (
          <div data-testid="open-rsvp-card">
            <RsvpCard
              rsvpData={rsvpData?.eventRsvpData || null}
              guestWedding={guestWedding as WeddingWebsiteQuery['guestWedding']}
              events={(eventData?.events as EventsQuery['events']) || null}
            />
            <div className={styles.cardSection}>
              {rsvpPageHidden ? (
                <div className={styles.linkContainer}>
                  <LinkButton onClick={onEditRsvpClick} data-testid="edit-rsvp-form" color="primary">
                    <Icon name="edit" size="md" className={styles.editIcon} />
                    Edit Form
                  </LinkButton>
                </div>
              ) : (
                <Action iconName="download" onClick={() => onDownloadRsvpResponses()} data-testid="edit-rsvp-form">
                  Download Responses
                </Action>
              )}
            </div>
          </div>
        ) : null}
      </Card>
    </CardContainer>
  );
};

export default RsvpPageCard;
